import {
  IonHeader,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  type RefresherEventDetail,
} from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Content } from "@/components/content";
import { UpcomingApplicationCardOverview } from "@/components/molecules/application/upcoming-application.card.overview";
import { UpcomingShiftCardOverview } from "@/components/molecules/shift/upcoming-shift-card.overview";
import { Toolbar } from "@/components/toolbar";
import { useMyApplications } from "@/lib/query/application/use-my-applications";
import { useMyShifts } from "@/lib/query/shift/use-my-shifts";

/**
 * Example of how to add a settings button in the header:
 *
 * @example
 *   <IonButtons collapse slot="primary">
 *     <Button
 *       routerLink={Routes.Settings()}
 *       routerDirection="forward"
 *       shape="round"
 *       color="light"
 *       size="small"
 *       className="touch-target min-h-0 part-icon:m-0 part-native:size-10 part-native:min-h-0 part-native:rounded-full part-native:bg-surface-secondary part-native:p-1 part-native:text-xs part-native:text-txt-primary"
 *     >
 *       <IonIcon slot="icon-only" icon={cog6ToothIcon} className="size-5" />
 *     </Button>
 *   </IonButtons>;
 */
export function UpcomingShiftsPage() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<"planned" | "registered">("planned");
  const { shifts, loadMore, hasNextPage, refetch: refetchShifts } = useMyShifts();
  const {
    applications,
    loadMore: loadMoreApplications,
    hasNextPage: hasNextPageApplications,
    refetch: refetchApplications,
  } = useMyApplications();

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    if (selectedTab === "planned") {
      await refetchShifts();
    } else {
      await refetchApplications();
    }
    event.detail.complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <Toolbar className="ion-p-2 plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),theme(spacing.4))]">
          <IonTitle data-ph-no-capture className="ion-text-brown-700 text-lg font-bold">
            {t("routes:upcoming-shifts.title")}
          </IonTitle>
        </Toolbar>
      </IonHeader>
      <Content>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle className="p-0 text-start text-3xl font-bold ion-text-txt-primary">
              {t("routes:upcoming-shifts.title")}
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonToolbar className="ion-no-padding">
          <IonSegment
            value={selectedTab}
            onIonChange={(e) => setSelectedTab(e.detail.value as "planned" | "registered")}
            className="grid w-full grid-cols-2 bg-surface-primary p-1"
          >
            <IonSegmentButton
              value="planned"
              className="w-full p-2 [&.segment-button-checked]:bg-brand-50 [&.segment-button-checked]:font-bold [&.segment-button-checked]:text-txt-brand [&:not(.segment-button-checked)]:text-txt-secondary"
            >
              Ingepland
            </IonSegmentButton>
            <IonSegmentButton
              value="registered"
              className="w-full p-2 [&.segment-button-checked]:bg-brand-50 [&.segment-button-checked]:font-bold [&.segment-button-checked]:text-txt-brand [&:not(.segment-button-checked)]:text-txt-secondary"
            >
              Aangemeld
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>

        <IonList key={selectedTab} className="flex flex-col gap-4">
          {selectedTab === "planned"
            ? shifts.map((shift) => <UpcomingShiftCardOverview key={shift?.id} shift={shift} />)
            : applications.map((application) => (
                <UpcomingApplicationCardOverview key={application.id} application={application} />
              ))}
        </IonList>

        <IonInfiniteScroll
          onIonInfinite={selectedTab === "planned" ? loadMore : loadMoreApplications}
          disabled={selectedTab === "planned" ? !hasNextPage : !hasNextPageApplications}
        >
          <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
        </IonInfiniteScroll>
      </Content>
    </IonPage>
  );
}
